// @givmo/app-admin — client entry (SPA boot).
//
// Boot sequence (browser-only; this is a static SPA, no SSR):
//   0. EP-OPS1 legacy-host autoforward (flag-gated): on `admin.givmo.io` with the forward enabled,
//      hand the visit to `employer.givmo.io` and boot NOTHING on the legacy origin;
//   1. read + validate the env subset (src/lib/env.ts, from Vite `import.meta.env`), and hand the same
//      env to the employer module, which never reads `import.meta.env` itself (EP-MNT3);
//   2. init client telemetry (Sentry + GA4) — day-one, no secrets;
//   3. init the Firebase Auth + App Check singletons from env;
//   4. configure the shared @givmo/api-client (base URL, `wb` prefix, Bearer + App Check providers);
//   4b. register the assistant entry point (a no-op while its switch is off);
//   5. create the QueryClient + router and render <App />.
//
// Steps 3–4 are the load-bearing integration the rest of the admin/super-admin rebuild reuses.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { configureEmployerModuleEnv } from '@givmo/employer-module/lib/env';
import { App } from './app';
import { computeLegacyAdminForwardUrl, readAdminEnv } from './lib/env';
import { initClientObservability } from './lib/telemetry';
import { initFirebaseRuntime } from './lib/firebase';
import { configureAdminClient, suiteSessionTransport } from './lib/api-client';
import { registerEmployerAssistant } from './features/assistant/assistant-mount';
import { createQueryClient } from './lib/query-client';
import { createAdminRouter } from './router/router';

import '@givmo/design-system/styles.css';
import './app.css';

// 0. Legacy-host autoforward (EP-OPS1, default off). `location.replace` leaves no history entry,
// so Back never bounces the user through the legacy host again. When forwarding, none of the boot
// steps run — no telemetry, no Firebase, no render on the origin being abandoned.
const forwardUrl = computeLegacyAdminForwardUrl(window.location);
if (forwardUrl !== null) {
  window.location.replace(forwardUrl);
} else {
  bootAdminApp();
}

function bootAdminApp(): void {
  // 1. Env — this app's reading of it, and the employer module's: its switches read the env the host
  // hands over, so this runs before anything below can render one of its pages
  // (`employer-module-env.test.ts` pins the order).
  const env = readAdminEnv();
  configureEmployerModuleEnv(import.meta.env);

  // 2. Telemetry (no-ops without a DSN / supported browser).
  initClientObservability(env);

  // 3. Firebase Auth + App Check from env.
  const { auth, appCheck } = initFirebaseRuntime(env);

  // 4. Configure the shared api-client singleton (every sdk.* call + query hook inherits it).
  configureAdminClient({ env, auth, appCheck });

  // 4b. Register the assistant into the shell's reserved slot (EP-AI1). A NO-OP while
  // VITE_EMPLOYER_ASSISTANT_ENABLED is off — nothing registers, so the top bar emits no assistant
  // DOM at all. It runs after step 4 because the panel's SSE headers come from the client just
  // configured there.
  registerEmployerAssistant();

  // 5. Render.
  const queryClient = createQueryClient();
  const router = createAdminRouter();

  const rootElement = document.getElementById('root');
  if (rootElement === null) {
    throw new Error('Root element #root not found.');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App
        auth={auth}
        queryClient={queryClient}
        router={router}
        suiteSession={suiteSessionTransport(env, appCheck)}
      />
    </StrictMode>,
  );
}
